import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Calculator as CalcIcon, X, GripHorizontal } from 'lucide-react';

/**
 * Small floating calculator: draggable anywhere (pointer events => works on
 * touch too), closable, and reopenable via a corner button. Basic arithmetic
 * with %, sign flip, backspace and memory (M+/M-/MR/MC). Keyboard-enabled while
 * open. Rendered once globally so it appears on every screen.
 */

type Op = '+' | '-' | '*' | '/' | null;

const fmt = (n: number): string => {
  if (!isFinite(n)) return 'Error';
  // trim floating noise, keep it compact
  const r = Math.round((n + Number.EPSILON) * 1e10) / 1e10;
  return String(r);
};

function FloatingCalculator() {
  const [open, setOpen] = useState(false);
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState<number | null>(null);
  const [op, setOp] = useState<Op>(null);
  const [fresh, setFresh] = useState(true); // next digit starts a new number
  const [memory, setMemory] = useState(0);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ dx: number; dy: number; active: boolean }>({ dx: 0, dy: 0, active: false });

  // --- calculator logic ---
  const inputDigit = useCallback((d: string) => {
    setDisplay((cur) => {
      if (fresh) { setFresh(false); return d; }
      if (cur === '0' && d === '0') return '0';
      if (cur === '0') return d;
      return cur.length < 16 ? cur + d : cur;
    });
  }, [fresh]);

  const inputDot = useCallback(() => {
    setDisplay((cur) => {
      if (fresh) { setFresh(false); return '0.'; }
      return cur.includes('.') ? cur : cur + '.';
    });
  }, [fresh]);

  const clearAll = useCallback(() => {
    setDisplay('0'); setPrev(null); setOp(null); setFresh(true);
  }, []);

  const backspace = useCallback(() => {
    setDisplay((cur) => (cur.length <= 1 || (cur.length === 2 && cur.startsWith('-')) ? '0' : cur.slice(0, -1)));
  }, []);

  const toggleSign = useCallback(() => {
    setDisplay((cur) => (cur === '0' ? cur : cur.startsWith('-') ? cur.slice(1) : '-' + cur));
  }, []);

  const percent = useCallback(() => {
    setDisplay((cur) => fmt(parseFloat(cur) / 100));
  }, []);

  const compute = (a: number, b: number, o: Op): number => {
    switch (o) {
      case '+': return a + b;
      case '-': return a - b;
      case '*': return a * b;
      case '/': return b === 0 ? NaN : a / b;
      default: return b;
    }
  };

  const chooseOp = useCallback((next: Op) => {
    const cur = parseFloat(display);
    if (prev !== null && op && !fresh) {
      const res = compute(prev, cur, op);
      setPrev(res); setDisplay(fmt(res));
    } else {
      setPrev(cur);
    }
    setOp(next); setFresh(true);
  }, [display, prev, op, fresh]);

  const equals = useCallback(() => {
    if (op === null || prev === null) return;
    const cur = parseFloat(display);
    const res = compute(prev, cur, op);
    setDisplay(fmt(res)); setPrev(null); setOp(null); setFresh(true);
  }, [display, prev, op]);

  const memoryDo = useCallback((kind: 'M+' | 'M-' | 'MR' | 'MC') => {
    const cur = parseFloat(display) || 0;
    if (kind === 'M+') setMemory((m) => m + cur);
    else if (kind === 'M-') setMemory((m) => m - cur);
    else if (kind === 'MC') setMemory(0);
    else if (kind === 'MR') { setDisplay(fmt(memory)); setFresh(true); }
  }, [display, memory]);

  // --- keyboard support while open ---
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      if (/[0-9]/.test(k)) { inputDigit(k); }
      else if (k === '.') inputDot();
      else if (k === '+' ) chooseOp('+');
      else if (k === '-') chooseOp('-');
      else if (k === '*') chooseOp('*');
      else if (k === '/') { e.preventDefault(); chooseOp('/'); }
      else if (k === '%') percent();
      else if (k === 'Enter' || k === '=') { e.preventDefault(); equals(); }
      else if (k === 'Backspace') backspace();
      else if (k === 'Escape') clearAll();
      else return;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, inputDigit, inputDot, chooseOp, percent, equals, backspace, clearAll]);

  // --- dragging (pointer events => mouse + touch) ---
  const clamp = (x: number, y: number) => {
    const el = panelRef.current;
    const w = el?.offsetWidth ?? 260;
    const h = el?.offsetHeight ?? 360;
    return {
      x: Math.max(6, Math.min(x, window.innerWidth - w - 6)),
      y: Math.max(6, Math.min(y, window.innerHeight - h - 6)),
    };
  };

  const onDragStart = (e: React.PointerEvent) => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    drag.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, active: true };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!drag.current.active) return;
    setPos(clamp(e.clientX - drag.current.dx, e.clientY - drag.current.dy));
  };
  const onDragEnd = (e: React.PointerEvent) => {
    drag.current.active = false;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  // keep it inside the viewport if the window resizes
  useEffect(() => {
    if (!open || !pos) return;
    const onResize = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, pos]);

  if (!open) {
    return (
      <button className="calc-fab" onClick={() => setOpen(true)} title="Calculator" aria-label="Open calculator">
        <CalcIcon size={22} />
      </button>
    );
  }

  const keys: { label: string; cls?: string; act: () => void }[] = [
    { label: 'MC', cls: 'mem', act: () => memoryDo('MC') },
    { label: 'MR', cls: 'mem', act: () => memoryDo('MR') },
    { label: 'M-', cls: 'mem', act: () => memoryDo('M-') },
    { label: 'M+', cls: 'mem', act: () => memoryDo('M+') },
    { label: 'C', cls: 'fn', act: clearAll },
    { label: '±', cls: 'fn', act: toggleSign },
    { label: '%', cls: 'fn', act: percent },
    { label: '÷', cls: 'op', act: () => chooseOp('/') },
    { label: '7', act: () => inputDigit('7') },
    { label: '8', act: () => inputDigit('8') },
    { label: '9', act: () => inputDigit('9') },
    { label: '×', cls: 'op', act: () => chooseOp('*') },
    { label: '4', act: () => inputDigit('4') },
    { label: '5', act: () => inputDigit('5') },
    { label: '6', act: () => inputDigit('6') },
    { label: '−', cls: 'op', act: () => chooseOp('-') },
    { label: '1', act: () => inputDigit('1') },
    { label: '2', act: () => inputDigit('2') },
    { label: '3', act: () => inputDigit('3') },
    { label: '+', cls: 'op', act: () => chooseOp('+') },
    { label: '0', cls: 'zero', act: () => inputDigit('0') },
    { label: '.', act: inputDot },
    { label: '⌫', cls: 'fn', act: backspace },
    { label: '=', cls: 'eq', act: equals },
  ];

  return (
    <div
      ref={panelRef}
      className="calc-panel"
      style={pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined}
      role="dialog"
      aria-label="Calculator"
    >
      <div
        className="calc-head"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
      >
        <GripHorizontal size={16} className="calc-grip" />
        <span className="calc-title">Calculator</span>
        {memory !== 0 && <span className="calc-memflag" title={`Memory: ${fmt(memory)}`}>M</span>}
        <button className="calc-close" onClick={() => setOpen(false)} aria-label="Close calculator">
          <X size={16} />
        </button>
      </div>

      <div className="calc-display">
        <div className="calc-op">{op ? `${fmt(prev ?? 0)} ${op === '*' ? '×' : op === '/' ? '÷' : op === '-' ? '−' : '+'}` : ''}</div>
        <div className="calc-value">{display}</div>
      </div>

      <div className="calc-keys">
        {keys.map((k) => (
          <button key={k.label} className={`calc-key ${k.cls || ''}`} onClick={k.act}>
            {k.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default FloatingCalculator;
